import { useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { t } from "i18next";
import cx from "classnames";
import FuzzySearch from "fuzzy-search";
import { ValidationResult } from "../../types/forms";
import {
  AutoComplete,
  DateInput,
  GenericDropDownList,
  Label,
  RadioButtonGroup,
  Avatar,
} from "../common";
import PeoplePicker from "../common/PeoplePicker";
import { AutoCompleteSelectedValue, KeyValuePair } from "../../types/generic";
import catchUpApi from "../../api/dashboard/catchUpApi";
import MeetingFormDto from "../../types/catch-ups/MeetingFormDto";
import MeetingFormFields from "../../types/catch-ups/MeetingFormFields";
import { CatchUpValidator, userDetailsHelper } from "../../helpers";
import ValidationWarning from "../common/ValidationWarning";
import UserContext from "../../state/UserContext";
import { BaseUserDetailsDto } from "../../types/dtos/generic";
import { useEffectOnce } from "react-use";
import MultipleChoiceOptionNumericId from "../../types/forms/MultipleChoiceOptions";
import UserSearchDto from "../../types/dtos/generic/UserSearchDto";
import { UsageScenario } from "../../types/generic/UsageScenario";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faCircleCheck,
} from "@fortawesome/pro-regular-svg-icons";
import FormRadioButtons from "../common/FormRadioButtons"; // Ensure this import is added

// Custom CSS for animations
const fadeInStyle = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }
  
  /* Custom focus style override */
  .no-focus-highlight:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: #e5e7eb !important; /* gray-200 */
    ring-color: transparent !important;
    ring-width: 0 !important;
  }
  
  .no-focus-highlight:focus-visible {
    outline: none !important;
    box-shadow: none !important;
    ring-width: 0 !important;
  }
`;

const participantInputId = "catch-up-participant";
const datePickerInputId = "catch-up-date";
const subjectFieldId = "catch-up-subject";
const subjectUserRadiosId = "catch-up-subject-user";

interface ManageCatchUpFormProps {
  formFieldValues: MeetingFormFields;
  onFormChange(newValues: MeetingFormFields): void;
  /** If you want custom content above the form fields, supply a value for this prop */
  customHeaderContent?: undefined | JSX.Element;
  /** If you want custom content/buttons below the form, supply a value for this prop */
  customFooterContent?: undefined | JSX.Element;
  /** If we already know who the catch up will be with, specify a value */
  otherParticipant?: BaseUserDetailsDto | undefined;
  /** If this catch up is already tied to an AnswerSet/CollabDoc, we can hide the subject field */
  answerSetUniqueId?: string | null | undefined;
  /** If this is from a journey or collab doc, a discussion might already exist. */
  discussionExists?: boolean | null | undefined;
  showValidationErrors: boolean;
  /** Indicates where this is being used so we can render the appropriate elements */
  usageScenario: UsageScenario;
}

const ManageCatchUpForm = ({
  formFieldValues,
  onFormChange,
  showValidationErrors,
  answerSetUniqueId = undefined,
  customHeaderContent = undefined,
  customFooterContent = undefined,
  otherParticipant = undefined,
  discussionExists,
  usageScenario,
}: ManageCatchUpFormProps) => {
  // Auth/API
  const auth = useAuth();
  const apiCatchUp = new catchUpApi(auth.user?.access_token);
  const userContext = useContext(UserContext);
  // State
  const [peoplePickerValue, setPeoplePickerValue] = useState<
    number | undefined
  >(otherParticipant?.userId);
  const [autoCompleteValue, setAutoCompleteValue] = useState<
    string | undefined
  >();
  const [
    selectedClientFormValueFromDropdown,
    setSelectedClientFormValueFromDropdown,
  ] = useState<string | undefined>();
  const [availableMeetingForms, setAvailableMeetingForms] = useState<
    MeetingFormDto[]
  >([]);
  const [subjectUserRadioOptions, setSubjectUserRadioOptions] = useState<
    MultipleChoiceOptionNumericId[]
  >([]);
  const [showSubjectUserRequiredWarning, setShowSubjectUserRequiredWarning] =
    useState(false);

  // State for storing user details for the cards
  const [meDetails, setMeDetails] = useState<UserCardDetails>({
    userId: userContext.user.id,
    firstName: userContext.user.firstName,
    fullName: userContext.user.fullName,
    initials:
      userContext.user.initials || userContext.user.fullName.substring(0, 2),
    profilePicUrl: userContext.user.profilePic,
  });
  const [otherUserDetails, setOtherUserDetails] =
    useState<UserCardDetails | null>(null);

  let autoCompleteSuggestionsList: KeyValuePair<string, string>[] = [];
  let clientFormDropdownList: KeyValuePair<number, string>[] = [];
  const genericCatchUpFormId = userContext.user.client.genericCatchUpFormId;

  if (availableMeetingForms) {
    autoCompleteSuggestionsList = availableMeetingForms.map((mf) => ({
      key: mf.formId.toString(),
      value: t(mf.formName),
    }));

    clientFormDropdownList = availableMeetingForms.map((mf) => ({
      key: mf.formId,
      value: t(mf.formName),
    }));
  }

  useEffectOnce(() => {
    // If the other participant is specified, we set the logged in user
    // as the subject user
    if (otherParticipant) {
      onFormChange({
        ...formFieldValues,
        subjectUserId: userContext.user.id,
        otherParticipantId: otherParticipant.userId,
      });
      setSubjectUserRadioButtonValues(
        otherParticipant.userId,
        otherParticipant.firstName,
        otherParticipant.fullName
      );
    }
  });

  useEffect(() => {
    const subjectUserFieldIsValid = validator.subjectUserFieldIsValid(
      formFieldValues.subjectUserId
    );
    if (subjectUserFieldIsValid) {
      getAutoCompleteFormSuggestions();
    }
  }, [formFieldValues.subjectUserId]);

  // Validation
  const subjectFieldIsRequired = answerSetUniqueId === undefined;
  const validator = new CatchUpValidator(subjectFieldIsRequired);

  const validationResults = {
    valid: new ValidationResult(true, []),
    invalid: new ValidationResult(false, [{ errorType: "REQUIRED" }]),
  };

  // Events
  const searchFormsBySearchTerms = (searchValue: string) => {
    if (!availableMeetingForms || availableMeetingForms.length === 0) {
      return [];
    }

    const searchSource = availableMeetingForms.map((mf) => {
      return {
        formId: mf.formId,
        formName: t(mf.formName),
        searchPhrases: mf.searchPhrases,
      };
    });

    const searcher = new FuzzySearch(
      searchSource,
      ["formName", "searchPhrases"],
      {
        caseSensitive: false,
        sort: true,
      }
    );
    var formMatches = searcher.search(searchValue);
    if (formMatches!.length > 0) {
      return formMatches!.map((mf) => ({
        key: mf.formId.toString(),
        value: t(mf.formName),
      }));
    }
    return [];
  };

  // Events
  const handleAutoCompleteValueSelected = (
    newValue: AutoCompleteSelectedValue | null
  ) => {
    if (!newValue) {
      return;
    }

    setAutoCompleteValue(newValue.displayText);

    if (!newValue.isCustomValue && newValue.key) {
      const formId = parseInt(newValue.key, 10);
      onFormChange({
        ...formFieldValues,
        subjectClientFormId: formId,
        title: newValue.displayText,
        isFreeTypedSubject: false,
      });
    }
  };

  const handleCustomTypedTitle = (customCatchUpTitle: string) => {
    if (genericCatchUpFormId) {
      const formId = genericCatchUpFormId;
      onFormChange({
        ...formFieldValues,
        subjectClientFormId: formId,
        title: customCatchUpTitle,
        isFreeTypedSubject: true,
      });
    }
  };

  const handleClientFormChangeFromDropdown = (selectedValue: string | null) => {
    if (selectedValue) {
      const formId = parseInt(selectedValue, 10);
      const catchUpTitle = clientFormDropdownList.find(
        (cf) => formId === cf.key
      )?.value;
      setSelectedClientFormValueFromDropdown(selectedValue);
      onFormChange({
        ...formFieldValues,
        subjectClientFormId: formId,
        title: catchUpTitle,
        isFreeTypedSubject: false,
      });
    }
  };

  function setSubjectUserRadioButtonValues(
    userId: number,
    firstName: string,
    fullName: string
  ) {
    // Get the display name for the selected user (full name if their first name matches the logged in user's)
    const otherUserDisplayName = userDetailsHelper.getDisplayName(userId, [
      {
        userId: userId,
        firstName: firstName,
        fullName: fullName,
      },
      {
        userId: userContext.user.id,
        firstName: userContext.user.firstName,
        fullName: userContext.user.fullName,
      },
    ]);

    // Create the radio button values
    const meOption: MultipleChoiceOptionNumericId = {
      isSelected: false,
      text: "Me",
      optionId: userContext.user.id,
      value: userContext.user.id,
      allowCustomText: false,
    };
    const themOption: MultipleChoiceOptionNumericId = {
      isSelected: false,
      text: otherUserDisplayName,
      optionId: userId,
      value: userId,
      allowCustomText: false,
    };

    // Update the state with the radio button options
    setSubjectUserRadioOptions([meOption, themOption]);
  }

  const handleParticipantChange = (selectedUser: UserSearchDto) => {
    setPeoplePickerValue(selectedUser.userId);

    // Store the selected user's details for use in the Avatar
    setOtherUserDetails({
      userId: selectedUser.userId,
      firstName: selectedUser.firstName,
      fullName: selectedUser.fullName,
      initials: selectedUser.initials || selectedUser.fullName.substring(0, 2),
      profilePicUrl: selectedUser.profileImageUrl || null,
    });

    setSubjectUserRadioButtonValues(
      selectedUser.userId,
      selectedUser.firstName,
      selectedUser.fullName
    );
  };

  const handleSubjectUserRadioChange = (
    radioOptions: MultipleChoiceOptionNumericId[]
  ) => {
    const selectedUser = radioOptions.find((ro) => ro.isSelected);
    const otherUser = radioOptions.find((ro) => !ro.isSelected);

    const selectedSubjectUserId = selectedUser
      ? Number(selectedUser.value)
      : undefined;
    const otherParticipantId = otherUser ? Number(otherUser.value) : undefined;

    // Update the radio button state so we can show which one is selected
    setSubjectUserRadioOptions([...radioOptions]);

    // Update the parent form state
    clearSubjectUserRequiredWarning();
    onFormChange({
      ...formFieldValues,
      subjectUserId: selectedSubjectUserId,
      otherParticipantId: otherParticipantId,
    });
  };

  // New function to handle card selection for subject user
  const handleSubjectUserCardSelection = (selectedUserId: number) => {
    // Find the other user
    const otherUserId = subjectUserRadioOptions.find(
      (opt) => opt.value !== selectedUserId
    )?.value;

    // Update the parent form state
    clearSubjectUserRequiredWarning();
    onFormChange({
      ...formFieldValues,
      subjectUserId: selectedUserId,
      otherParticipantId: otherUserId ? Number(otherUserId) : undefined,
    });

    // Update the radio options for other scenarios that still use radio buttons
    const updatedOptions = subjectUserRadioOptions.map((opt) => ({
      ...opt,
      isSelected: opt.value === selectedUserId,
    }));
    setSubjectUserRadioOptions(updatedOptions);
  };

  const handleTargetDateAndTimeChange = (newValue: Date | null) => {
    onFormChange({
      ...formFieldValues,
      targetDate: newValue ? newValue : undefined,
    });
  };

  const getAutoCompleteFormSuggestions = () => {
    // Call the API to load the necessary state
    if (!formFieldValues.subjectUserId) return;

    const successCallback = (meetingForms: MeetingFormDto[]) => {
      setAvailableMeetingForms(meetingForms);
    };
    const errorCallback = (error: any) => {
      console.error(error);
    };

    apiCatchUp.GetAvailableMeetingForms(
      formFieldValues.subjectUserId,
      successCallback,
      errorCallback
    );
  };

  const peoplePickerIsValid = Number(peoplePickerValue) > 0;
  const subjectUserIsValid =
    validator.subjectUserFieldIsValid(formFieldValues.subjectUserId) &&
    validator.participantFieldIsValid(formFieldValues.otherParticipantId);
  const subjectUserSelectionIsVisible =
    !answerSetUniqueId &&
    (otherParticipant != null || (peoplePickerIsValid && otherUserDetails != null));
  const isSubjectFieldBlocked =
    subjectUserSelectionIsVisible && !subjectUserIsValid;
  const showSubjectUserFieldValidation =
    (showValidationErrors || showSubjectUserRequiredWarning) &&
    !validator.subjectUserFieldIsValid(formFieldValues.subjectUserId) &&
    subjectUserSelectionIsVisible;

  const handleBlockedSubjectFieldInteraction = () => {
    if (
      subjectUserSelectionIsVisible &&
      !validator.subjectUserFieldIsValid(formFieldValues.subjectUserId)
    ) {
      setShowSubjectUserRequiredWarning(true);
    }
  };

  const clearSubjectUserRequiredWarning = () => {
    setShowSubjectUserRequiredWarning(false);
  };

  const timingOptions = [
    {
      optionId: "NOW",
      text: "Now",
      isSelected: formFieldValues.timingPreference === "NOW",
      value: "NOW",
      allowCustomText: false,
    },
    {
      optionId: "FUTURE",
      text: "Schedule for Later",
      isSelected: formFieldValues.timingPreference === "FUTURE",
      value: "FUTURE",
      allowCustomText: false,
    },
  ];

  return (
    <>
      <style>{fadeInStyle}</style>
      {/* Display for 'Dashboard' usage */}
      {usageScenario === "DASHBOARD" && (
        <>
          <div className="p-4">
            <Label
              text="When would you like to have this Catch up?"
              className="text-gray-700 font-medium"
            />

            {/* Validation warning for timing preference */}
            {showValidationErrors && !formFieldValues.timingPreference && (
              <ValidationWarning
                isValid={false}
                errors={validationResults.invalid.errors}
              />
            )}

            {/* Replace the Timing option cards section */}
            <FormRadioButtons
              uniqueFieldName="timingPreference"
              values={timingOptions}
              onChange={(updatedValues) => {
                const selectedOption = updatedValues.find(
                  (option) => option.isSelected
                );
                if (selectedOption) {
                  onFormChange({
                    ...formFieldValues,
                    timingPreference: selectedOption.value as
                      | "NOW"
                      | "FUTURE"
                      | undefined,
                  });
                }
              }}
              layout="HORIZONTAL"
              showValidationErrors={showValidationErrors}
              validationResult={validationResults.valid}
              maxTextLengthBeforeForcingVerticalLayout={20}
            />

            <div className="text-gray-700 mt-4">
              {!answerSetUniqueId && (
                <>
                  {!otherParticipant && (
                    <div className="mb-4">
                      <Label
                        htmlFor={participantInputId}
                        text={t("CatchUps.Popup.WhoWith")}
                        className="text-gray-700 font-medium"
                      />
                      <div className="mt-2">
                        <PeoplePicker
                          inputId={participantInputId}
                          className="block p-2 w-full border border-gray-200 bg-gray-50 hover:bg-gray-100 focus:bg-white rounded-md transition-colors duration-200 focus:ring-0 focus:outline-none no-focus-highlight"
                          nonMultiSelectChangeValueType="USER-DTO"
                          onPeoplePickerChange={handleParticipantChange}
                          showValidationErrors={showValidationErrors}
                          excludeLoggedInUserFromResults={true}
                          validationResult={
                            peoplePickerIsValid
                              ? validationResults.valid
                              : validationResults.invalid
                          }
                        />
                      </div>
                    </div>
                  )}

                  {peoplePickerIsValid && otherUserDetails && (
                    <div className="mb-4">
                      <Label
                        htmlFor={subjectUserRadiosId}
                        text="Who is this about?"
                        className="text-gray-700 font-medium"
                      />
                      {showSubjectUserFieldValidation && (
                        <ValidationWarning
                          isValid={false}
                          errors={validationResults.invalid.errors}
                        />
                      )}
                      {/* Card style subject selection for Dashboard */}
                      {subjectUserRadioOptions.length > 0 && (
                        <div className="mt-2 flex flex-col sm:flex-row gap-4 mb-2">
                          {/* "Me" Card */}
                          <UserCard
                            userDetails={meDetails}
                            isSelected={
                              formFieldValues.subjectUserId === meDetails.userId
                            }
                            onClick={() =>
                              handleSubjectUserCardSelection(meDetails.userId)
                            }
                          />

                          {/* Other User Card */}
                          <UserCard
                            userDetails={otherUserDetails}
                            isSelected={
                              formFieldValues.subjectUserId ===
                              otherUserDetails.userId
                            }
                            onClick={() =>
                              handleSubjectUserCardSelection(
                                otherUserDetails.userId
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="mb-4">
                <Label
                  htmlFor={subjectFieldId}
                  text={t("CatchUps.Popup.Subject")}
                  className="text-gray-700 font-medium"
                />
                {showValidationErrors &&
                  !validator.subjectFieldIsValid(
                    formFieldValues.subjectClientFormId
                  ) && (
                    <ValidationWarning
                      isValid={false}
                      errors={validationResults.invalid.errors}
                    />
                  )}
                {/**If the client has a generic catch up form set up allow the user to type and give them suggestions.  If there isnt a clientFormId show a dropdown*/}
                <div className="relative mt-2">
                  <div
                    className={
                      isSubjectFieldBlocked ? "pointer-events-none" : undefined
                    }
                  >
                    {genericCatchUpFormId && (
                      <AutoComplete
                        selectedValue={autoCompleteValue}
                        onValueSelected={handleAutoCompleteValueSelected}
                        handleCustomTypedTitle={handleCustomTypedTitle}
                        valuesList={autoCompleteSuggestionsList}
                        placeholder="Enter a topic you'd like to discuss..."
                        className="block p-3 w-full border border-gray-200 bg-gray-50 hover:bg-gray-100 focus:bg-white rounded-md outline-none focus:ring-0 transition-colors duration-200 no-focus-highlight"
                        inputId="autocomplete-input"
                        suggestionPropertyToDisplay="VALUE"
                        allowCustomTypedValues={true}
                        customSearchFunction={searchFormsBySearchTerms}
                        disabled={!subjectUserIsValid}
                      />
                    )}
                    {!genericCatchUpFormId && (
                      <GenericDropDownList
                        currentValue={
                          selectedClientFormValueFromDropdown
                            ? selectedClientFormValueFromDropdown
                            : null
                        }
                        items={clientFormDropdownList}
                        onChange={handleClientFormChangeFromDropdown}
                        className="block w-full mt-2 transition-colors duration-200 no-focus-highlight"
                        bgColorClassName="border border-gray-200 bg-gray-50 hover:bg-gray-100 focus:bg-white focus:ring-0 focus:outline-none"
                        inputId={subjectFieldId}
                        showValidationErrors={showValidationErrors}
                      />
                    )}
                  </div>
                  {isSubjectFieldBlocked && (
                    <button
                      type="button"
                      className="absolute inset-0 z-10 cursor-not-allowed"
                      onClick={handleBlockedSubjectFieldInteraction}
                      aria-label="Select who this catch up is about first"
                    />
                  )}
                </div>
              </div>

              {/* Date field last when scheduling for later so the calendar opens over the footer, not the subject field */}
              {formFieldValues.timingPreference === "FUTURE" && (
                <div className="mb-4 animate-fade-in">
                  <Label
                    htmlFor={datePickerInputId}
                    text="When would you like to schedule this for?"
                    className="text-gray-700 font-medium"
                  />
                  {showValidationErrors &&
                    !validator.dateFieldIsValid(formFieldValues.targetDate) && (
                      <ValidationWarning
                        isValid={false}
                        errors={validationResults.invalid.errors}
                      />
                    )}
                  <div className="flex flex-row w-full gap-4">
                    <DateInput
                      onChange={handleTargetDateAndTimeChange}
                      inputId={datePickerInputId}
                      showValidationErrors={showValidationErrors}
                      value={formFieldValues.targetDate}
                      showTimeSelect={true}
                      placeholder="Select a date and time"
                      allowDatesInPast={false}
                      className="block w-full border border-gray-200 bg-gray-50 hover:bg-gray-100 focus:bg-white rounded-md transition-colors duration-200 focus:ring-0 focus:outline-none no-focus-highlight"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* "Now" information banner */}
            {formFieldValues.timingPreference === "NOW" && (
              <div className="bg-sky-50 rounded-lg p-4 mb-4 mt-4">
                <div className="flex gap-3">
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className="text-sky-600 mt-0.5"
                  />
                  <p className="text-sm text-sky-700">
                    This will be created immediately. Once you hit save, you'll
                    be redirected to start filling it out.
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Display for 'Journey/Collab Doc' usage */}
      {usageScenario === "JOURNEY-COLLAB-DOC" && (
        <>
          {customHeaderContent ? customHeaderContent : null}
          {discussionExists == true && (
            <div>
              You already have a meeting scheduled for this. You can change the
              date of this below.
            </div>
          )}
          {!answerSetUniqueId && (
            <>
              {!otherParticipant && (
                <div className="mb-2">
                  <Label
                    htmlFor={participantInputId}
                    text={t("CatchUps.Popup.WhoWith")}
                  />
                  <PeoplePicker
                    inputId={participantInputId}
                    className="block mt-2 p-2 w-full bg-gray-100 border-0"
                    nonMultiSelectChangeValueType="USER-DTO"
                    onPeoplePickerChange={handleParticipantChange}
                    showValidationErrors={showValidationErrors}
                    excludeLoggedInUserFromResults={true}
                    validationResult={
                      peoplePickerIsValid
                        ? validationResults.valid
                        : validationResults.invalid
                    }
                  />
                </div>
              )}

              {peoplePickerIsValid && (
                <div className="mb-2">
                  <Label
                    htmlFor={subjectUserRadiosId}
                    text="Who is this about?"
                  />
                  {showSubjectUserFieldValidation && (
                    <ValidationWarning
                      isValid={false}
                      errors={validationResults.invalid.errors}
                    />
                  )}
                  <RadioButtonGroup
                    uniqueFieldName={subjectUserRadiosId}
                    values={subjectUserRadioOptions}
                    onChange={handleSubjectUserRadioChange}
                    layout="HORIZONTAL"
                    containerClassNames="mt-1"
                    showValidationErrors={
                      showValidationErrors || showSubjectUserRequiredWarning
                    }
                    validationResult={
                      subjectUserIsValid
                        ? validationResults.valid
                        : validationResults.invalid
                    }
                  />
                </div>
              )}
            </>
          )}

          <div className="mb-2">
            <Label
              htmlFor={datePickerInputId}
              text={t("CatchUps.Popup.TargetDate")}
              className={cx(
                !answerSetUniqueId ? "font-medium text-gray-700" : "text-white"
              )}
            />
            {showValidationErrors &&
              !validator.dateFieldIsValid(formFieldValues.targetDate) && (
                <ValidationWarning
                  isValid={false}
                  errors={validationResults.invalid.errors}
                />
              )}
            <div className="flex flex-row w-full gap-4">
              <DateInput
                onChange={handleTargetDateAndTimeChange}
                inputId={datePickerInputId}
                showValidationErrors={showValidationErrors}
                value={formFieldValues.targetDate}
                showTimeSelect={true}
                placeholder="Select a date and time"
                allowDatesInPast={false}
              />
            </div>
          </div>
          {!answerSetUniqueId && (
            <div>
              <Label
                htmlFor={subjectFieldId}
                text={t("CatchUps.Popup.Subject")}
              />
              {showValidationErrors &&
                !validator.subjectFieldIsValid(
                  formFieldValues.subjectClientFormId
                ) && (
                  <ValidationWarning
                    isValid={false}
                    errors={validationResults.invalid.errors}
                  />
                )}
              {/**If the client has a generic catch up form set up allow the user to type and give them suggestions.  If there isnt a clientFormId show a dropdown*/}
              <div className="relative mt-2">
                <div
                  className={
                    isSubjectFieldBlocked ? "pointer-events-none" : undefined
                  }
                >
                  {genericCatchUpFormId && (
                    <AutoComplete
                      selectedValue={autoCompleteValue}
                      onValueSelected={handleAutoCompleteValueSelected}
                      handleCustomTypedTitle={handleCustomTypedTitle}
                      valuesList={autoCompleteSuggestionsList}
                      placeholder=""
                      className="text-gray-400 block p-2 w-full bg-gray-100 border-0 rounded-md outline-none focus:ring-0"
                      inputId="autocomplete-input-journey"
                      suggestionPropertyToDisplay="VALUE"
                      allowCustomTypedValues={true}
                      customSearchFunction={searchFormsBySearchTerms}
                      disabled={!subjectUserIsValid}
                    />
                  )}
                  {!genericCatchUpFormId && (
                    <GenericDropDownList
                      currentValue={
                        selectedClientFormValueFromDropdown
                          ? selectedClientFormValueFromDropdown
                          : null
                      }
                      items={clientFormDropdownList}
                      onChange={handleClientFormChangeFromDropdown}
                      className="block w-full"
                      bgColorClassName="bg-gray-100"
                      inputId={subjectFieldId}
                      showValidationErrors={showValidationErrors}
                    />
                  )}
                </div>
                {isSubjectFieldBlocked && (
                  <button
                    type="button"
                    className="absolute inset-0 z-10 cursor-not-allowed"
                    onClick={handleBlockedSubjectFieldInteraction}
                    aria-label="Select who this catch up is about first"
                  />
                )}
              </div>
            </div>
          )}
          {customFooterContent ? customFooterContent : null}
        </>
      )}
    </>
  );
};

interface UserCardDetails {
  userId: number;
  firstName: string;
  fullName: string;
  initials: string;
  profilePicUrl: string | null;
}

const UserCard = ({
  userDetails,
  isSelected,
  onClick,
}: {
  userDetails: UserCardDetails;
  isSelected: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={onClick}
      className={cx(
        "group relative flex items-center px-4 py-3 border rounded-lg cursor-pointer flex-1 transition-all duration-200 text-left",
        isSelected
          ? "border-primary bg-white shadow-md ring-1 ring-primary ring-opacity-30"
          : "border-gray-200 hover:border-gray-300 hover:shadow-md hover:ring-1 hover:ring-gray-300"
      )}
    >
      <div className="flex items-center w-full">
        <div className="mr-3 transform transition-transform duration-200 hover:scale-110">
          <Avatar
            imageUrl={userDetails.profilePicUrl}
            userFullName={userDetails.fullName}
            userInitials={userDetails.initials}
            size={8}
          />
        </div>
        <span
          className={cx(
            "text-sm font-medium flex-grow",
            isSelected ? "text-primary font-semibold" : "text-gray-700"
          )}
        >
          {userDetails.firstName}
        </span>
        <div
          className={cx(
            "min-w-[28px] flex items-center justify-end",
            isSelected ? "text-primary" : "text-transparent"
          )}
        >
          <FontAwesomeIcon icon={faCircleCheck} className="h-5 w-5" />
        </div>
      </div>
    </button>
  );
};

export default ManageCatchUpForm;
